import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getTarifas, updateTarifas,
  getPremiosAdmin, createPremio, updatePremio, deletePremio,
  getRoutes, getEmployees,
} from "../services/api";
import { DollarSign, Save, MapPin, Award, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Car } from "lucide-react";

const DEFAULT_ZONAS = { "1": "", "2": "", "3": "", "4": "" };
const DEFAULT_VEHICULO = { bicicleta: "", moto: "", auto: "" };
const DEFAULT_TARIFAS = { zonas_porcentaje: { ...DEFAULT_ZONAS }, precio_por_vehiculo: { ...DEFAULT_VEHICULO } };
const VEHICULO_LABEL = { bicicleta: "Bicicleta", moto: "Moto", auto: "Auto" };
const VEHICULO_EMOJI = { bicicleta: "🚲", moto: "🏍", auto: "🚗" };
const EMPTY_PREMIO = { nombre: "", tipo_periodo: "diario", fecha_inicio: "", fecha_fin: "", alcance: "global", route_id: "", user_id: "", medidores: "", monto: "", activo: true };
const PERIODO_LABEL = { diario: "Por día", semanal: "Por semana", mensual: "Por mes", rango: "Rango de fechas" };

function formatARS(value) {
  const num = parseFloat(value);
  return isNaN(num) ? "" : num.toLocaleString("es-AR");
}

function fromApiZonas(zp) {
  const src = zp || {};
  return { "1": src["1"] != null ? String(src["1"]) : "", "2": src["2"] != null ? String(src["2"]) : "", "3": src["3"] != null ? String(src["3"]) : "", "4": src["4"] != null ? String(src["4"]) : "" };
}

function fromApiVehiculo(pv) {
  const src = pv || {};
  return { bicicleta: src.bicicleta != null ? String(src.bicicleta) : "", moto: src.moto != null ? String(src.moto) : "", auto: src.auto != null ? String(src.auto) : "" };
}

export default function TarifasPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";

  // ── Tarifas ──
  const [form, setForm] = useState(DEFAULT_TARIFAS);
  const [loadingTarifas, setLoadingTarifas] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tarifaError, setTarifaError] = useState("");

  // ── Premios ──
  const [premios, setPremios] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loadingPremios, setLoadingPremios] = useState(true);
  const [premioError, setPremioError] = useState("");

  const [premioModal, setPremioModal] = useState(null); // null | { mode, id? }
  const [premioForm, setPremioForm] = useState(EMPTY_PREMIO);
  const [premioFormError, setPremioFormError] = useState("");
  const [savingPremio, setSavingPremio] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    getTarifas()
      .then((data) => setForm({ zonas_porcentaje: fromApiZonas(data.zonas_porcentaje), precio_por_vehiculo: fromApiVehiculo(data.precio_por_vehiculo) }))
      .catch(() => setTarifaError("Error al cargar tarifas"))
      .finally(() => setLoadingTarifas(false));

    loadPremios();
    getRoutes().then(setRoutes).catch(() => {});
    getEmployees().then(setEmployees).catch(() => {});
  }, []);

  async function loadPremios() {
    setLoadingPremios(true);
    setPremioError("");
    try { setPremios(await getPremiosAdmin()); }
    catch (err) { setPremioError(err.message); }
    finally { setLoadingPremios(false); }
  }

  // ── Tarifas handlers ──
  function setField(key, value) { setForm((f) => ({ ...f, [key]: value })); setSaved(false); }
  function setZona(z, value) { setForm((f) => ({ ...f, zonas_porcentaje: { ...f.zonas_porcentaje, [z]: value } })); setSaved(false); }
  function setVehiculo(v, value) { setForm((f) => ({ ...f, precio_por_vehiculo: { ...f.precio_por_vehiculo, [v]: value } })); setSaved(false); }

  async function handleSave() {
    setSaving(true); setTarifaError(""); setSaved(false);
    try {
      const zp = form.zonas_porcentaje || {};
      const pv = form.precio_por_vehiculo || {};
      await updateTarifas({
        precio_por_medidor: 0,
        objetivo_diario: 0, premio_objetivo_diario: 0,
        objetivo_mensual: 0, premio_objetivo_mensual: 0,
        premios_escala: [],
        zonas_porcentaje: { "1": parseFloat(zp["1"]) || 0, "2": parseFloat(zp["2"]) || 0, "3": parseFloat(zp["3"]) || 0, "4": parseFloat(zp["4"]) || 0 },
        precio_por_vehiculo: { bicicleta: parseFloat(pv.bicicleta) || 0, moto: parseFloat(pv.moto) || 0, auto: parseFloat(pv.auto) || 0 },
      });
      setSaved(true);
    } catch (err) { setTarifaError(err.message); }
    finally { setSaving(false); }
  }

  // ── Premios handlers ──
  function openCreatePremio() { setPremioForm(EMPTY_PREMIO); setPremioFormError(""); setPremioModal({ mode: "create" }); }

  function openEditPremio(p) {
    const alcance = p.user_id ? "usuario" : p.route_id ? "ruta" : "global";
    setPremioForm({ nombre: p.nombre, tipo_periodo: p.tipo_periodo, fecha_inicio: p.fecha_inicio ? p.fecha_inicio.slice(0, 10) : "", fecha_fin: p.fecha_fin ? p.fecha_fin.slice(0, 10) : "", alcance, route_id: p.route_id ? String(p.route_id) : "", user_id: p.user_id ? String(p.user_id) : "", medidores: String(p.medidores), monto: String(p.monto), activo: p.activo });
    setPremioFormError(""); setPremioModal({ mode: "edit", id: p.id });
  }

  async function handleSavePremio() {
    if (!premioForm.nombre.trim()) { setPremioFormError("El nombre es requerido."); return; }
    if (!premioForm.medidores || !premioForm.monto) { setPremioFormError("La meta y el monto son requeridos."); return; }
    if (premioForm.tipo_periodo === "rango" && (!premioForm.fecha_inicio || !premioForm.fecha_fin)) { setPremioFormError("Ingresá las fechas de inicio y fin."); return; }
    if (premioForm.alcance === "ruta" && !premioForm.route_id) { setPremioFormError("Seleccioná una ruta."); return; }
    if (premioForm.alcance === "usuario" && !premioForm.user_id) { setPremioFormError("Seleccioná un usuario."); return; }

    setSavingPremio(true); setPremioFormError("");
    try {
      const payload = {
        nombre: premioForm.nombre.trim(),
        tipo_periodo: premioForm.tipo_periodo,
        fecha_inicio: premioForm.tipo_periodo === "rango" ? premioForm.fecha_inicio : null,
        fecha_fin: premioForm.tipo_periodo === "rango" ? premioForm.fecha_fin : null,
        route_id: premioForm.alcance === "ruta" ? parseInt(premioForm.route_id) : null,
        user_id: premioForm.alcance === "usuario" ? premioForm.user_id : null,
        medidores: parseInt(premioForm.medidores),
        monto: parseFloat(premioForm.monto),
        activo: premioForm.activo,
      };
      if (premioModal.mode === "create") await createPremio(payload);
      else await updatePremio(premioModal.id, payload);
      setPremioModal(null);
      loadPremios();
    } catch (err) { setPremioFormError(err.message); }
    finally { setSavingPremio(false); }
  }

  async function handleDeletePremio() {
    setDeleting(true);
    try { await deletePremio(deleteTarget.id); setDeleteTarget(null); loadPremios(); }
    catch (err) { setPremioError(err.message); }
    finally { setDeleting(false); }
  }

  async function toggleActivo(p) {
    try { await updatePremio(p.id, { activo: !p.activo }); setPremios((prev) => prev.map((x) => x.id === p.id ? { ...x, activo: !p.activo } : x)); }
    catch { loadPremios(); }
  }

  function periodoLabel(p) {
    if (p.tipo_periodo === "rango" && p.fecha_inicio && p.fecha_fin)
      return `${p.fecha_inicio.slice(0, 10)} → ${p.fecha_fin.slice(0, 10)}`;
    return PERIODO_LABEL[p.tipo_periodo] || p.tipo_periodo;
  }

  if (loadingTarifas) return <div style={s.page}><div style={s.loadingWrap}>Cargando...</div></div>;

  return (
    <div style={s.page}>
      {/* ── Header tarifas ── */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Tarifas</h1>
          <p style={s.subtitle}>Configurá el precio base y las zonas</p>
        </div>
        {isAdminPlus && (
          <button style={{ ...s.btnSave, ...(saving ? s.btnSaveDisabled : {}) }} onClick={handleSave} disabled={saving}>
            <Save size={15} style={{ marginRight: 7 }} />
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        )}
      </div>

      {tarifaError && <div style={s.errorBanner}>{tarifaError}</div>}
      {saved && <div style={s.successBanner}>✓ Tarifas guardadas correctamente</div>}

      <div style={s.grid}>
        {/* ── Precio por vehículo ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <Car size={18} color="#0ea5e9" />
            <span style={s.cardTitle}>Precio por vehículo</span>
          </div>
          <p style={s.zonaHint}>Precio base por medidor según el vehículo del auditor. Si está en 0 se usa el precio base general.</p>
          <div style={s.row2}>
            {["bicicleta", "moto", "auto"].map((v) => {
              const precio = parseFloat(form.precio_por_vehiculo?.[v]);
              return (
                <div key={v} style={s.field}>
                  <label style={s.label}>{VEHICULO_EMOJI[v]} {VEHICULO_LABEL[v]}</label>
                  <div style={s.inputPrefix}>
                    <span style={s.prefix}>$</span>
                    <input style={s.inputWithPrefix} type="number" min="0" step="0.01" value={form.precio_por_vehiculo?.[v] ?? ""} onChange={(e) => setVehiculo(v, e.target.value)} placeholder="0" disabled={!isAdminPlus} />
                  </div>
                  {precio > 0 && <p style={s.hint}>Vale <strong>${formatARS(precio)}</strong> por medidor</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Zonas ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <MapPin size={18} color="#f59e0b" />
            <span style={s.cardTitle}>Incremento por zona</span>
          </div>
          <p style={s.zonaHint}>% extra sobre el precio base según la zona asignada a la ruta</p>
          <div style={s.row2}>
            {["1", "2", "3", "4"].map((z) => {
              const pct = parseFloat(form.zonas_porcentaje?.[z]);
              return (
                <div key={z} style={s.field}>
                  <label style={s.label}>Zona {z}</label>
                  <div style={s.inputSuffix}>
                    <input style={s.inputWithSuffix} type="number" min="0" max="200" step="0.1" value={form.zonas_porcentaje?.[z] ?? ""} onChange={(e) => setZona(z, e.target.value)} placeholder="0" disabled={!isAdminPlus} />
                    <span style={s.suffix}>%</span>
                  </div>
                  {pct > 0 && (
                    <p style={s.hint}>
                      {["bicicleta","moto","auto"].map((v) => {
                        const base = parseFloat(form.precio_por_vehiculo?.[v]);
                        if (!base) return null;
                        return <span key={v} style={{ display: "block" }}>{VEHICULO_EMOJI[v]} ${(base * (1 + pct / 100)).toLocaleString("es-AR")}</span>;
                      })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Premios ── */}
      <div style={{ ...s.card, marginTop: 20, gridColumn: "1 / -1" }}>
        <div style={s.cardHeader}>
          <Award size={18} color="#9333ea" />
          <span style={s.cardTitle}>Premios</span>
          {isAdminPlus && (
            <button style={s.btnAdd} onClick={openCreatePremio}>
              <Plus size={13} style={{ marginRight: 4 }} />
              Nuevo premio
            </button>
          )}
        </div>

        {premioError && <div style={{ ...s.errorBanner, marginBottom: 12 }}>{premioError}</div>}

        {loadingPremios ? (
          <p style={s.emptyText}>Cargando premios...</p>
        ) : premios.length === 0 ? (
          <p style={s.emptyText}>No hay premios configurados.{isAdminPlus && ' Hacé clic en "Nuevo premio" para comenzar.'}</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {["Nombre", "Período", "Alcance", "Meta", "Monto", "Activo"].map((h) => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                  {isAdminPlus && <th style={s.th} />}
                </tr>
              </thead>
              <tbody>
                {premios.map((p) => (
                  <tr key={p.id} style={s.tr}>
                    <td style={s.td}><strong>{p.nombre}</strong></td>
                    <td style={s.td}><span style={s.periodoBadge}>{periodoLabel(p)}</span></td>
                    <td style={s.td}>
                      {p.user_id
                        ? <span style={s.usuarioBadge}>👤 {p.user_name || "Usuario"}</span>
                        : p.route_id
                          ? <span style={s.rutaBadge}>{p.route_name || `Ruta #${p.route_id}`}</span>
                          : <span style={s.globalBadge}>Global</span>}
                    </td>
                    <td style={s.td}>{p.medidores} medidores</td>
                    <td style={{ ...s.td, fontWeight: 700, color: "#16a34a" }}>${parseFloat(p.monto).toLocaleString("es-AR")}</td>
                    <td style={s.td}>
                      {isAdminPlus ? (
                        <button style={s.toggleBtn} onClick={() => toggleActivo(p)}>
                          {p.activo ? <ToggleRight size={22} color="#16a34a" /> : <ToggleLeft size={22} color="#94a3b8" />}
                        </button>
                      ) : (
                        <span style={{ color: p.activo ? "#16a34a" : "#94a3b8", fontWeight: 700 }}>{p.activo ? "Sí" : "No"}</span>
                      )}
                    </td>
                    {isAdminPlus && (
                      <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                        <button style={s.actionBtn} onClick={() => openEditPremio(p)}><Edit2 size={14} /></button>
                        <button style={{ ...s.actionBtn, color: "#dc2626" }} onClick={() => setDeleteTarget(p)}><Trash2 size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!isAdminPlus && <p style={s.readonlyNotice}>Solo los administradores Plus pueden modificar las tarifas y premios.</p>}

      {/* ── Modal crear/editar premio ── */}
      {premioModal && (
        <div style={s.overlay} onClick={() => setPremioModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>{premioModal.mode === "create" ? "Nuevo premio" : "Editar premio"}</span>
              <button style={s.modalClose} onClick={() => setPremioModal(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <FormField label="Nombre *">
                <input style={s.input} autoFocus placeholder="Ej: Premio semana activa" value={premioForm.nombre} onChange={(e) => setPremioForm((f) => ({ ...f, nombre: e.target.value }))} />
              </FormField>

              <FormField label="Período *">
                <select style={s.input} value={premioForm.tipo_periodo} onChange={(e) => setPremioForm((f) => ({ ...f, tipo_periodo: e.target.value }))}>
                  <option value="diario">Por día</option>
                  <option value="semanal">Por semana</option>
                  <option value="mensual">Por mes</option>
                  <option value="rango">Rango de fechas</option>
                </select>
              </FormField>

              {premioForm.tipo_periodo === "rango" && (
                <div style={s.rowTwo}>
                  <FormField label="Fecha inicio *">
                    <input style={s.input} type="date" value={premioForm.fecha_inicio} onChange={(e) => setPremioForm((f) => ({ ...f, fecha_inicio: e.target.value }))} />
                  </FormField>
                  <FormField label="Fecha fin *">
                    <input style={s.input} type="date" value={premioForm.fecha_fin} onChange={(e) => setPremioForm((f) => ({ ...f, fecha_fin: e.target.value }))} />
                  </FormField>
                </div>
              )}

              <FormField label="Alcance *">
                <div style={s.alcanceRow}>
                  {[
                    { val: "global",  label: "Global" },
                    { val: "ruta",    label: "Por ruta" },
                    { val: "usuario", label: "Por usuario" },
                  ].map(({ val, label }) => (
                    <button key={val} style={{ ...s.alcanceBtn, ...(premioForm.alcance === val ? s.alcanceBtnActive : {}) }} onClick={() => setPremioForm((f) => ({ ...f, alcance: val, route_id: "", user_id: "" }))}>
                      {label}
                    </button>
                  ))}
                </div>
              </FormField>

              {premioForm.alcance === "ruta" && (
                <FormField label="Ruta *">
                  <select style={s.input} value={premioForm.route_id} onChange={(e) => setPremioForm((f) => ({ ...f, route_id: e.target.value }))}>
                    <option value="">Seleccioná una ruta...</option>
                    {routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </FormField>
              )}

              {premioForm.alcance === "usuario" && (
                <FormField label="Usuario *">
                  <select style={s.input} value={premioForm.user_id} onChange={(e) => setPremioForm((f) => ({ ...f, user_id: e.target.value }))}>
                    <option value="">Seleccioná un usuario...</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </FormField>
              )}

              <div style={s.rowTwo}>
                <FormField label="Meta de medidores *">
                  <input style={s.input} type="number" min="1" placeholder="Ej: 100" value={premioForm.medidores} onChange={(e) => setPremioForm((f) => ({ ...f, medidores: e.target.value }))} />
                </FormField>
                <FormField label="Monto del premio *">
                  <div style={s.inputPrefix}>
                    <span style={s.prefix}>$</span>
                    <input style={s.inputWithPrefix} type="number" min="0" step="0.01" placeholder="0" value={premioForm.monto} onChange={(e) => setPremioForm((f) => ({ ...f, monto: e.target.value }))} />
                  </div>
                </FormField>
              </div>

              <div style={s.activoRow} onClick={() => setPremioForm((f) => ({ ...f, activo: !f.activo }))}>
                {premioForm.activo ? <ToggleRight size={24} color="#16a34a" /> : <ToggleLeft size={24} color="#94a3b8" />}
                <span style={s.activoLabel}>{premioForm.activo ? "Activo" : "Inactivo"}</span>
              </div>

              {premioFormError && <div style={s.errorBanner}>{premioFormError}</div>}

              <button style={{ ...s.btnSave, width: "100%", justifyContent: "center" }} onClick={handleSavePremio} disabled={savingPremio}>
                {savingPremio ? "Guardando..." : premioModal.mode === "create" ? "Crear premio" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmar eliminación ── */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <div style={{ ...s.modal, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>Eliminar premio</span>
              <button style={s.modalClose} onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: 14, color: "var(--text)", marginBottom: 20 }}>
                ¿Eliminás el premio <strong>{deleteTarget.nombre}</strong>? Esta acción no se puede deshacer.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...s.btnSave, flex: 1, background: "#dc2626", justifyContent: "center" }} onClick={handleDeletePremio} disabled={deleting}>
                  {deleting ? "Eliminando..." : "Eliminar"}
                </button>
                <button style={s.btnCancel} onClick={() => setDeleteTarget(null)}>Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  page: { width: "100%" },
  loadingWrap: { padding: 40, color: "var(--text-2)", textAlign: "center" },

  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 },
  title: { fontSize: 26, fontWeight: 700, color: "var(--text)", margin: "0 0 4px" },
  subtitle: { fontSize: 13, color: "var(--text-2)", margin: 0 },

  btnSave: { display: "inline-flex", alignItems: "center", padding: "10px 20px", borderRadius: 10, border: "none", background: "#D4620D", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14, fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap" },
  btnSaveDisabled: { opacity: 0.65, cursor: "not-allowed" },
  btnCancel: { flex: 1, padding: "10px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-2)", fontWeight: 700, cursor: "pointer", fontSize: 14, fontFamily: "Montserrat, sans-serif" },

  errorBanner: { background: "var(--error-bg)", color: "var(--error-text)", borderRadius: 10, padding: "10px 16px", fontSize: 13, fontWeight: 700, marginBottom: 16 },
  successBanner: { background: "var(--success-bg)", color: "var(--success-text)", borderRadius: 10, padding: "10px 16px", fontSize: 13, fontWeight: 700, marginBottom: 16 },

  grid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 },
  zonaHint: { fontSize: 12, color: "var(--text-2)", margin: "0 0 16px", lineHeight: 1.5 },

  card: { borderRadius: 14, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", background: "var(--bg-card)" },
  cardHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "var(--text)", flex: 1 },

  field: { marginBottom: 16 },
  label: { display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 7 },
  hint: { fontSize: 12, color: "var(--text-2)", margin: "6px 0 0" },

  inputPrefix: { display: "flex", alignItems: "center", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" },
  inputSuffix: { display: "flex", alignItems: "center", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" },
  prefix: { padding: "10px 12px", background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 14, fontWeight: 700, borderRight: "1px solid var(--border)", flexShrink: 0 },
  suffix: { padding: "10px 12px", background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, fontWeight: 700, borderLeft: "1px solid var(--border)", flexShrink: 0 },
  inputWithPrefix: { flex: 1, border: "none", outline: "none", padding: "10px 12px", fontSize: 14, fontFamily: "Montserrat, sans-serif", background: "transparent", width: "100%" },
  inputWithSuffix: { flex: 1, border: "none", outline: "none", padding: "10px 12px", fontSize: 14, fontFamily: "Montserrat, sans-serif", background: "transparent", width: "100%" },

  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },

  // Premios table
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--text-2)", borderBottom: "1px solid var(--border)", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" },
  tr: { borderBottom: "1px solid var(--border)" },
  td: { padding: "11px 14px", color: "var(--text)", verticalAlign: "middle" },
  emptyText: { color: "var(--text-2)", fontSize: 13, textAlign: "center", padding: "24px 0 8px", margin: 0 },

  periodoBadge: { background: "#ede9fe", color: "#7c3aed", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 },
  globalBadge:  { background: "var(--bg-subtle)", color: "var(--text-2)", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 },
  rutaBadge:    { background: "var(--primary-bg)", color: "#D4620D", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 },
  usuarioBadge: { background: "#eff6ff", color: "#1d4ed8", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 },

  toggleBtn: { background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" },
  actionBtn: { background: "none", border: "none", cursor: "pointer", color: "var(--text-2)", padding: "4px 6px", borderRadius: 6, display: "inline-flex", alignItems: "center" },

  btnAdd: { display: "inline-flex", alignItems: "center", padding: "6px 13px", borderRadius: 10, border: "none", background: "#ede9fe", color: "#7c3aed", fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "Montserrat, sans-serif" },

  // Modal
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24 },
  modal: { background: "var(--bg-card)", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 16px 48px rgba(0,0,0,0.25)" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px 16px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 15, fontWeight: 700, color: "var(--text)" },
  modalClose: { background: "none", border: "none", fontSize: 22, color: "var(--text-2)", cursor: "pointer", lineHeight: 1, padding: "0 4px" },
  modalBody: { padding: 20 },

  input: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--border)", fontSize: 13, color: "var(--text)", background: "var(--bg-card)", fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
  rowTwo: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },

  alcanceRow: { display: "flex", gap: 8 },
  alcanceBtn: { flex: 1, padding: "9px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-2)", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit", transition: "all .15s" },
  alcanceBtnActive: { background: "#D4620D", color: "#fff", borderColor: "#D4620D" },

  activoRow: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 16 },
  activoLabel: { fontSize: 13, fontWeight: 600, color: "var(--text)" },

  readonlyNotice: { marginTop: 24, textAlign: "center", color: "var(--text-2)", fontSize: 13, fontStyle: "italic" },
};
